import { ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';

/**
 * Client-side fetch composable for `apexl/io-dynamic-components`' data
 * source registry — the Vue half of the "hybrid pattern" described on
 * `StatTile`/`ActivityFeedTile`/etc.'s PHP docblocks: the PHP `Component`
 * bakes only the source slug + display config, and the Vue tile calls
 * this itself to get the live value.
 *
 * Hits `GET /api/v1/data-sources/{slug}/fetch` (registered by
 * `apexl/io-page-layouts`'s `routes/data/page-layouts.php` against
 * `apexl\Io\modules\dynamicComponents\Controller\DataSourceFetchController`),
 * which returns `{shape, value, meta, fetched_at}`.
 *
 * Deliberately NOT a full SWR/store-invalidation layer (no such primitive
 * exists yet in `@apexl/icarus-core` or this project — the "gets SWR
 * caching for free" aspiration in the PHP docblocks is aspirational, not
 * wired up). This is the minimal, correct fetch-on-mount + manual
 * `refresh()` shape; upgrading to shared cache + `X-Invalidates`-driven
 * invalidation is a follow-up, not something this hook should half-fake.
 *
 * @param {string} slug Data source registry key (e.g. `shell.platform.users_count`).
 * @param {Array|Object} params Passed through as the `params` JSON blob
 *        (matches `DataSourceFetchController::extractParams()` layer 1 —
 *        the catch-all for source-specific keys).
 */
// `@apexl/icarus-core`'s `useApi()` axios instances carry no request
// timeout of their own (verified against the shipped 3.1.x source — neither
// `createApi()` nor the internal token-refresh call sets one), so a stalled
// connection on any of these calls would otherwise hang forever with no
// client-side recovery. The dashboard mounts several `StatTile`s at once,
// each independently reaching this hook — an explicit per-call timeout
// here bounds the worst case to a failed tile (caught by the existing
// try/catch/finally below) instead of a page that never finishes loading.
const REQUEST_TIMEOUT_MS = 8000;

function useDataSource(slug, params = []) {
  const api = useApi();

  const value = ref(null);
  const meta = ref(null);
  const shape = ref(null);
  const loading = ref(false);
  const error = ref(null);

  async function refresh() {
    if (!slug) {
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      const hasParams = Array.isArray(params) ? params.length > 0 : Object.keys(params || {}).length > 0;
      const query = hasParams ? { params: JSON.stringify(params) } : {};

      const { data } = await api.get(`/api/v1/data-sources/${slug}/fetch`, { params: query, timeout: REQUEST_TIMEOUT_MS });

      value.value = data?.value ?? null;
      meta.value = data?.meta ?? null;
      shape.value = data?.shape ?? null;
    } catch (err) {
      error.value = err;
    } finally {
      loading.value = false;
    }
  }

  refresh();

  return {
    value,
    meta,
    shape,
    loading,
    error,
    refresh,
  };
}

export default useDataSource;
